/**
Copyright (c) 2022-2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  CpuChipIcon,
  ServerStackIcon,
  CircleStackIcon,
  ArrowPathIcon,
  FireIcon,
  BoltIcon,
  SignalIcon,
  SignalSlashIcon,
} from '@heroicons/react/24/outline';
import { containerService } from '../../services/api';

interface GPUStats {
  index: number;
  name: string;
  memory_used_mb: number;
  memory_total_mb: number;
  memory_usage_percent: number;
  utilization_percent: number;
  temperature: number;
  power_usage_watts: number;
}

interface PodStats {
  cpu_usage_millicores: number;
  cpu_limit_millicores: number;
  cpu_usage_percent: number;
  memory_usage_bytes: number;
  memory_limit_bytes: number;
  memory_usage_mb: number;
  memory_usage_percent: number;
  storage_usage_bytes: number;
  storage_total_bytes: number;
  storage_usage_mb: number;
  storage_usage_percent: number;
  gpu_stats?: GPUStats[];
  pod_name: string;
  namespace: string;
  node_name: string;
  status: string;
  restarts: number;
}

interface StatsMessage {
  type: 'connected' | 'stats' | 'error';
  stats?: PodStats;
  timestamp?: string;
  message?: string;
  error?: string;
  pod?: string;
  resources?: {
    cpu_cores: number;
    memory_gb: number;
    storage_gb: number;
    gpu_count: number;
    gpu_type: string;
    gpu_fraction: number;
  };
}

interface ContainerStatsProps {
  containerId: string;
  projectId: string;
  isRunning: boolean;
  allocatedCpu: number;
  allocatedMemoryGb: number;
  allocatedStorageGb: number;
  gpuCount?: number;
  gpuFraction?: number;
  gpuType?: string;
}

const ContainerStats: React.FC<ContainerStatsProps> = ({
  containerId,
  projectId,
  isRunning,
  allocatedCpu,
  allocatedMemoryGb,
  allocatedStorageGb,
  gpuCount,
  gpuFraction,
  gpuType,
}) => {
  const [stats, setStats] = useState<PodStats | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [podName, setPodName] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if container has GPU
  const hasGPU = (gpuCount && gpuCount > 0) || (gpuFraction && gpuFraction > 0);

  // Connect to WebSocket for streaming stats
  const connect = useCallback(() => {
    if (!isRunning) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setIsConnecting(true);
    setError(null);

    const wsUrl = containerService.getStatsStreamUrl(containerId, projectId, 2); // 2 second interval

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const message: StatsMessage = JSON.parse(event.data);

          switch (message.type) {
            case 'connected':
              if (message.pod) {
                setPodName(message.pod);
              }
              break;
            case 'stats':
              if (message.stats) {
                setStats(message.stats);
                setLastUpdate(new Date());
              }
              break;
            case 'error':
              setError(message.error || 'Unknown error occurred');
              break;
          }
        } catch {
          console.error('Failed to parse stats message');
        }
      };

      ws.onerror = () => {
        setError('WebSocket connection error');
        setIsConnected(false);
        setIsConnecting(false);
      };

      ws.onclose = () => {
        setIsConnected(false);
        setIsConnecting(false);

        // Attempt to reconnect after 5 seconds if still running
        if (isRunning) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 5000);
        }
      };
    } catch (err) {
      setError('Failed to connect to stats stream');
      setIsConnecting(false);
    }
  }, [containerId, projectId, isRunning]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Connect when component mounts or isRunning changes
  useEffect(() => {
    if (isRunning) {
      connect();
    } else {
      disconnect();
      setStats(null);
    }

    return () => {
      disconnect();
    };
  }, [isRunning, connect, disconnect]);

  // Format bytes to human readable
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  // Progress bar component
  const ProgressBar = ({ value, color }: { value: number; color: string }) => (
    <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
      <div
        className={`h-full ${color} transition-all duration-500`}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );

  // Get color based on usage percentage
  const getUsageColor = (percent: number): string => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (!isRunning) {
    return (
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <CpuChipIcon className="w-5 h-5 text-gray-400" />
          <h3 className="text-base font-semibold text-white">Infrastructure Stats</h3>
        </div>
        <div className="text-center py-6 bg-gray-800 rounded-lg">
          <CpuChipIcon className="w-10 h-10 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Container is not running</p>
          <p className="text-xs text-gray-400">Start the container to view stats</p>
        </div>
      </div>
    );
  }

  if (isConnecting && !stats) {
    return (
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <CpuChipIcon className="w-5 h-5 text-gray-400" />
          <h3 className="text-base font-semibold text-white">Infrastructure Stats</h3>
        </div>
        <div className="flex items-center justify-center py-6">
          <ArrowPathIcon className="w-5 h-5 text-gray-400 animate-spin" />
          <span className="ml-2 text-sm text-gray-400">Connecting...</span>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CpuChipIcon className="w-5 h-5 text-gray-400" />
            <h3 className="text-base font-semibold text-white">Infrastructure Stats</h3>
          </div>
          <button
            onClick={connect}
            className="text-xs text-primary-500 hover:text-primary-400 flex items-center gap-1"
          >
            <ArrowPathIcon className="w-3 h-3" />
            Retry
          </button>
        </div>
        <div className="text-center py-6 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
          <SignalSlashIcon className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
          <p className="text-sm text-yellow-400">Connection failed</p>
          <p className="text-xs text-yellow-400 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Use backend-computed storage percentage (based on actual filesystem size from df)
  const storagePercent = stats?.storage_usage_percent ?? 0;
  const storageTotalBytes = stats?.storage_total_bytes ?? (allocatedStorageGb * 1024 * 1024 * 1024);

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CpuChipIcon className="w-5 h-5 text-blue-500" />
          <h3 className="text-base font-semibold text-white">Infrastructure Stats</h3>
          {isConnected ? (
            <span className="flex items-center gap-1 text-xs text-green-500 bg-green-500/20 px-1.5 py-0.5 rounded-full">
              <SignalIcon className="w-3 h-3" />
              Live
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-yellow-500 bg-yellow-500/20 px-1.5 py-0.5 rounded-full">
              <SignalSlashIcon className="w-3 h-3" />
              ...
            </span>
          )}
        </div>
        {lastUpdate && (
          <span className="text-xs text-gray-400">
            {lastUpdate.toLocaleTimeString()}
          </span>
        )}
      </div>

      {stats ? (
        <div className="space-y-3">
          {/* Pod Info - Compact */}
          <div className="p-2 bg-gray-800 rounded-lg text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div className="truncate">
                <span className="text-gray-400">Pod: </span>
                <span className="font-mono text-gray-300" title={stats.pod_name || podName || ''}>
                  {(stats.pod_name || podName || '').slice(-20)}
                </span>
              </div>
              <div className="truncate">
                <span className="text-gray-400">Node: </span>
                <span className="font-mono text-gray-300" title={stats.node_name}>
                  {stats.node_name}
                </span>
              </div>
            </div>
          </div>

          {/* CPU */}
          <div className="p-2.5 bg-blue-500/20 rounded-lg">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <CpuChipIcon className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-white">CPU</span>
              </div>
              <span className="text-sm font-semibold text-blue-500">
                {stats.cpu_usage_percent.toFixed(1)}%
              </span>
            </div>
            <ProgressBar value={stats.cpu_usage_percent} color={getUsageColor(stats.cpu_usage_percent)} />
            <div className="mt-1 flex justify-between text-xs text-gray-400">
              <span>{(stats.cpu_usage_millicores / 1000).toFixed(2)} cores</span>
              <span>{allocatedCpu} allocated</span>
            </div>
          </div>

          {/* Memory */}
          <div className="p-2.5 bg-purple-500/20 rounded-lg">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <ServerStackIcon className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-medium text-white">Memory</span>
              </div>
              <span className="text-sm font-semibold text-purple-500">
                {stats.memory_usage_percent.toFixed(1)}%
              </span>
            </div>
            <ProgressBar value={stats.memory_usage_percent} color={getUsageColor(stats.memory_usage_percent)} />
            <div className="mt-1 flex justify-between text-xs text-gray-400">
              <span>{formatBytes(stats.memory_usage_bytes)}</span>
              <span>{allocatedMemoryGb} GB</span>
            </div>
          </div>

          {/* Storage */}
          <div className="p-2.5 bg-orange-500/20 rounded-lg">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <CircleStackIcon className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium text-white">Storage</span>
              </div>
              <span className="text-sm font-semibold text-orange-500">
                {storagePercent.toFixed(1)}%
              </span>
            </div>
            <ProgressBar value={storagePercent} color={getUsageColor(storagePercent)} />
            <div className="mt-1 flex justify-between text-xs text-gray-400">
              <span>{formatBytes(stats.storage_usage_bytes)}</span>
              <span>{formatBytes(storageTotalBytes)}</span>
            </div>
          </div>

          {/* GPU Stats */}
          {stats.gpu_stats && stats.gpu_stats.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-gray-400 flex items-center gap-1">
                <CpuChipIcon className="w-3.5 h-3.5 text-green-500" />
                GPU {gpuType && <span className="font-normal">({gpuType})</span>}
              </h4>
              {stats.gpu_stats.map((gpu) => (
                <div key={gpu.index} className="p-2.5 bg-green-500/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-white truncate max-w-[60%]" title={gpu.name}>
                      {gpu.name}
                    </span>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="flex items-center gap-0.5 text-orange-500" title="Temp">
                        <FireIcon className="w-3 h-3" />
                        {gpu.temperature}°
                      </span>
                      <span className="flex items-center gap-0.5 text-blue-500" title="Power">
                        <BoltIcon className="w-3 h-3" />
                        {gpu.power_usage_watts.toFixed(0)}W
                      </span>
                    </div>
                  </div>

                  {/* GPU Utilization */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-1 text-xs">
                      <span className="text-gray-400">Utilization</span>
                      <span className="font-semibold text-green-500">{gpu.utilization_percent}%</span>
                    </div>
                    <ProgressBar value={gpu.utilization_percent} color={getUsageColor(gpu.utilization_percent)} />
                  </div>

                  {/* GPU Memory */}
                  <div>
                    <div className="flex items-center justify-between mb-1 text-xs">
                      <span className="text-gray-400">Memory</span>
                      <span className="font-semibold text-green-500">{gpu.memory_usage_percent.toFixed(1)}%</span>
                    </div>
                    <ProgressBar value={gpu.memory_usage_percent} color={getUsageColor(gpu.memory_usage_percent)} />
                    <div className="mt-1 flex justify-between text-xs text-gray-400">
                      <span>{gpu.memory_used_mb} MB</span>
                      <span>{gpu.memory_total_mb} MB</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Show message if GPU expected but not found */}
          {hasGPU && (!stats.gpu_stats || stats.gpu_stats.length === 0) && (
            <div className="p-2.5 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
              <div className="flex items-center gap-2 text-xs text-yellow-400">
                <CpuChipIcon className="w-4 h-4" />
                <span>GPU stats unavailable</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center py-6">
          <ArrowPathIcon className="w-5 h-5 text-gray-400 animate-spin" />
          <span className="ml-2 text-sm text-gray-400">Loading stats...</span>
        </div>
      )}
    </div>
  );
};

export default ContainerStats;
